import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Alert,
  Box,
  Paper
} from '@mui/material';

const VerifyEmail: React.FC = () => {
  const { userProfile, sendVerificationEmail, logout, refreshUser } = useAuth();
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check if email is verified
  useEffect(() => {
    if (userProfile?.emailVerified) {
      navigate('/');
    }
  }, [userProfile?.emailVerified, navigate]);

  const handleResendVerification = async () => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      await sendVerificationEmail();
      setSuccess('Verification email sent! Please check your inbox.');
    } catch (err) {
      setError('Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await logout();
      navigate('/login');
    } catch (err) {
      setError('Failed to logout. Please try again.');
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Refresh the user's profile
      await refreshUser();
      
      // Check verification status
      if (userProfile?.emailVerified) {
        setSuccess('Email verified successfully! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        setError('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (err) {
      setError('Failed to check verification status. Please try again.');
      console.error('Error checking verification:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Verify Your Email
          </Typography>
          
          <Typography variant="body1" paragraph align="center">
            Please verify your email address ({userProfile?.email}) to gain full access.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleResendVerification}
              disabled={loading}
              fullWidth
            >
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </Button>

            <Button
              variant="contained"
              color="success"
              onClick={handleCheckVerification}
              disabled={loading}
              fullWidth
            >
              {loading ? 'Checking...' : "I've Verified My Email"}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleLogout}
              disabled={loading}
              fullWidth
            >
              Logout
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }} align="center">
            Note: The verification link will expire in 15 minutes.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmail; 